// "Type X to confirm" for things that can't be undone: deleting an account or
// a workspace, here and in the console. A 401 means the server wants a recent
// sign-in (spec §15.1), so it offers one, coming back here afterwards.
import { ApiError, goToSignIn } from '../api.ts';
import { h } from '../util/dom.ts';
import { busy, formError } from './page.ts';

export interface TypedConfirm {
  title: string;
  /** Paragraphs saying what will happen. */
  explain: (Node | string)[][];
  /** What must be typed (exactly, ignoring surrounding spaces). */
  confirmText: string;
  /** The button's label, e.g. "Delete workspace". */
  action: string;
  /** Does it; throws to show an error and keep the dialog open. */
  run: () => Promise<void>;
}

export function typedConfirm(o: TypedConfirm): void {
  const input = h('input', { type: 'text', autocomplete: 'off', spellcheck: 'false', 'aria-label': `Type ${o.confirmText} to confirm` });
  const go = h('button', { type: 'submit', class: 'btn danger' }, [o.action]);
  go.disabled = true;
  const cancel = h('button', { type: 'button', class: 'btn' }, ['Cancel']);
  const problem = formError();
  const form = h('form', { class: 'stack' }, [
    ...o.explain.map((p) => h('p', {}, p)),
    h('label', { class: 'field' }, [h('span', { class: 'field-label' }, ['Type ', h('code', {}, [o.confirmText]), ' to confirm']), input]),
    problem, h('div', { class: 'dialog-actions' }, [cancel, go]),
  ]);
  const dialog = h('dialog', { class: 'dialog', 'aria-labelledby': 'confirm-title' }, [h('h2', { id: 'confirm-title' }, [o.title]), form]);
  input.addEventListener('input', () => { go.disabled = input.value.trim() !== o.confirmText; });
  cancel.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => dialog.remove());
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void busy(go, async () => {
      try {
        await o.run();
        dialog.close();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          const again = h('button', { type: 'button', class: 'btn small primary' }, ['Sign in again']);
          again.addEventListener('click', goToSignIn);
          problem.replaceChildren(document.createTextNode(`${err.message} `), again);
          problem.hidden = false;
        } else problem.show(err);
      }
    });
  });
  document.body.append(dialog);
  dialog.showModal();
  input.focus();
}
